<template>
  <div class="d-flex align-items-center justify-content-center p-4 h-100">
    <changeable-status :state="isSaving" :counter="changeCounter" fixed />

    <div class="rounded card d-flex flex-column justify-content-center align-items-center gap-4 h-75 py-10 px-6 col-xl-5 col-lg-6 col-md-6 col-sm-9">
      <vacancy-header v-if="internalVacancy" :item="internalVacancy" />

      <!-- Not accepting applications -->
      <template v-if="blockingApplications">
          <div class="d-flex flex-column justify-content-center align-items-center gap-4 h-75 py-10 px-6">

            <div class="d-flex justify-content-center">
              <ExclamationCircleIcon class="text-interactive-danger" size="3x" />
            </div>

            <h3 class="text-2xl font-weight-bolder m-0">
              {{ $t("application.candidacy_failure") }}
            </h3>

            <span>{{ $t("errors.enroll.application-closed") }}</span>

            <div class="align-items-center">
              <span>{{ $t("errors.enroll.maintain-updated") }}</span>
              <b-button variant="primary" class="mt-3" pill block :to="{ name: 'vacancy-search' }">
                {{ $t("application.searchForVacancies") }}
              </b-button>
            </div>
        </div>
      </template>

      <!-- Already applied -->
      <template v-else-if="blockingAlreadyApplied">
        <div class="d-flex flex-column justify-content-center align-items-center gap-4 h-75 py-10 px-6">

          <CheckCircleIcon size="3.25x" class="icon-success" />

          <h3 class="text-2xl font-weight-bolder m-0">
            {{ $t("application.confirmed_enrollment") }}
          </h3>
          
          <p class="m-0">
            {{ $t("errors.enroll.already-applied") }}
          </p>

          <div class="align-items-center">
            <span>{{ $t("errors.enroll.maintain-updated") }}</span>
            <div class="divider"><hr class="w-100" /></div>
            <b-button variant="primary" pill block :to="{ name: 'profile-edit' }" >
              {{ $t("application.fill_my_curriculum") }}
            </b-button>
          </div>
        </div>
      </template>

      <!-- Enroll workflow -->
      <template v-else>

        <!-- Enrolling -->
        <template v-if="isEnrolling">
          <div class="d-flex flex-column justify-content-center align-items-center gap-4 h-75 py-10 px-6">
            <b-spinner class="text-black" style="opacity: 0.1" />
            <h3 class="text-2xl font-weight-bolder m-0">
              {{ $t("application.confirming_enrollment") }}
            </h3>
            <p class="text-subdued m-0">
              {{ $t("application.we_wish_you_very_luck") }}
            </p>
          </div>
        </template>

        <!-- Enrolled -->
        <template v-else>
          <div
            class="d-flex flex-column justify-content-center align-items-stretch gap-4 h-75 py-10 px-6 w-100"
            v-if="requireOrigin"
          >
            <h3 class="text-2xl font-weight-bolder m-0 text-center">
              {{ $t("application.origin_question") }}
            </h3>
            <p class="text-subdued m-0 text-center">
              {{ $t("application.origin_question_hint") }}
            </p>

            <validation-observer ref="originRules">
              <form-field-radio
                name="candidature_origin_id"
                v-model="candidatureOriginId"
                :options="originOptions"
                rules="required"
                stacked
              />
            </validation-observer>

            <b-button variant="primary" pill block @click="confirmOrigin()">
              {{ $t("application.conclude_application") }}
            </b-button>
          </div>
          <div class="d-flex flex-column justify-content-center align-items-center gap-4 h-75 py-10 px-6" v-else-if="success">
            <CheckCircleIcon size="3.25x" class="icon-success" />

            <h3 class="text-2xl font-weight-bolder m-0">
              {{ $t("application.confirmed_enrollment") }}
            </h3>
            <p class="text-subdued">
              {{ $t("application.you_can_now_do_the_next_steps") }}
            </p>

            <b-button variant="primary" pill block @click="goToApplication">{{
              $t("application.see_next_steps")
            }}</b-button>
            <b-button
              variant="outline-primary"
              pill
              class="border"
              block
              :to="{ name: 'profile-edit' }"
              >{{ $t("application.fill_my_curriculum") }}</b-button
            >
          </div>
          <div v-else class="d-flex flex-column justify-content-center align-items-center gap-4 h-75 py-10 px-6 card-body">
            <div class="d-flex justify-content-center">
              <ExclamationCircleIcon class="text-interactive-danger" size="3x" />
            </div>

            <h3 class="text-2xl font-weight-bolder m-0" v-if="alreadApplied">
              {{ $t("errors.enroll.already-applied-title") }}
            </h3>
            <h3 class="text-2xl font-weight-bolder m-0" v-else>
              {{ $t("application.candidacy_failure") }}
            </h3>

            <div class="align-items-center" v-if="alreadApplied">
              <span>{{ $t("errors.enroll.already-applied") }}</span>
              <span class="d-block mt-3">{{ $t("errors.enroll.maintain-updated") }}</span>
              
              <b-button variant="primary" class="mt-4" pill block :to="{ name: 'vacancy-search' }">
                {{ $t("application.searchForVacancies") }}
              </b-button>
              <b-button
                variant="outline-primary"
                pill
                class="border mt-2"
                block
                :to="{ name: 'profile-edit' }"
                >{{ $t("application.fill_my_curriculum") }}</b-button
              >
            </div>

            <div class="align-items-center" v-else>
              <span v-if="isEnrollBlocked">
                {{ $t("errors.enroll.does-not-attend") }}
              </span>
              <ul class="my-4" :class="{ 'list-unstyled': !isEnrollBlocked }">
                <li v-for="(item, index) in messages" :key="index">
                  <span v-html="getTranslatedHelp(item)"></span>
                </li>
              </ul>
              <span v-if="isEnrollBlocked">
                {{ $t("errors.enroll.maintain-updated") }}
              </span>

              <div class="divider"><hr class="w-100" /></div>

              <validation-observer ref="enrollStepRules">
                <CompanyForm v-bind="form" v-if="hasFields" />

                <div class="d-flex flex-column gap-2 my-4" v-if="hasPcd">
                  <form-set-pcd
                    v-if="sections.personal.pcd.show"
                    v-model="curriculum.personal"
                    :fields="sections.personal.pcd.fields"
                  />
                </div>

                <div
                  class="d-flex flex-column gap-2 my-4"
                  v-if="hasProfessionalSection"
                >
                  <h4>
                    {{ $t("curriculum.sections.professional.experiences") }}
                  </h4>

                  <div class="d-flex justify-content-start gap-2">
                    <form-field-radio
                      name="curriculum.professional.has_professional_history"
                      v-model="curriculum.professional.has_professional_history"
                      :options="domain.domains.has_letter_attribute"
                      autocomplete="off"
                      rules="required"
                    />
                  </div>
                  <form-set-professional-experience
                    v-if="
                      sections.professional.experiences.show &&
                      curriculum.professional.has_professional_history == 'y'
                    "
                    form-id="express"
                    name="curriculum.experiences"
                    v-model="curriculum.experiences"
                    :fields="sections.professional.experiences.fields"
                    section="experiences"
                  />
                </div>
                <div
                  class="d-flex flex-column gap-2 my-4"
                  v-if="hasAcademicSection"
                >
                  <h4>{{ $t("curriculum.sections.education.academic") }}</h4>

                  <div class="d-flex justify-content-start gap-2">
                    <form-field-radio
                      name="curriculum.education.has_schooling_level"
                      v-model="curriculum.education.has_schooling_level"
                      :options="domain.domains.has_letter_attribute"
                      autocomplete="off"
                      rules="required"
                    />
                  </div>
                  <form-set-academic
                    v-if="
                      sections.education.academic.show &&
                      curriculum.education.has_schooling_level == 'y'
                    "
                    name="curriculum.academic"
                    v-model="curriculum.academic"
                    :fields="sections.education.academic.fields"
                    section="academic"
                  />
                </div>
                <div
                  class="d-flex flex-column gap-2 my-4"
                  v-if="hasLanguagesSection"
                >
                  <h4>{{ $t("curriculum.sections.education.languages") }}</h4>
                  <div class="d-flex justify-content-start gap-2">
                    <form-field-radio
                      name="curriculum.education.has_languages"
                      v-model="curriculum.education.has_languages"
                      :options="domain.domains.has_letter_attribute"
                      autocomplete="off"
                      rules="required"
                    />
                  </div>

                  <form-set-languages
                    v-if="
                      sections.education.languages.show &&
                      curriculum.education.has_languages == 'y'
                    "
                    name="curriculum.languages"
                    v-model="curriculum.languages"
                    :fields="sections.education.languages.fields"
                    section="languages"
                  />
                </div>
                <div
                  class="d-flex flex-column gap-2 my-4"
                  v-if="hasComputingSection"
                >
                  <h4>{{ $t("curriculum.sections.education.computing") }}</h4>
                  <div class="d-flex justify-content-start gap-2">
                    <form-field-radio
                      name="curriculum.education.has_computer_knowledge"
                      v-model="curriculum.education.has_computer_knowledge"
                      :options="domain.domains.has_letter_attribute"
                      autocomplete="off"
                      rules="required"
                    />
                  </div>

                  <form-set-computing
                    v-if="
                      sections.education.computing.show &&
                      curriculum.education.has_computer_knowledge == 'y'
                    "
                    name="curriculum.computing"
                    v-model="curriculum.computing"
                    :fields="sections.education.computing.fields"
                    section="computing"
                  />
                </div>
              </validation-observer>

              <b-button variant="primary" pill block @click="continueEnroll()">
                {{ $t("common.complete_curriculum") }}
              </b-button>
            </div>

            <div class="align-items-center" v-else>
              <span v-if="isEnrollBlocked">
                {{ $t("errors.enroll.maintain-updated") }}
              </span>

              <div class="divider"><hr class="w-100" /></div>

              <b-button variant="primary" pill block :to="{ name: 'vacancy-search' }">
                {{ $t("application.searchForVacancies") }}
              </b-button>
            </div>
          </div>
        </template>
      </template>
    </div>

    <!--<NpsWidget />-->
  </div>
</template>

<script>
import * as _ from "lodash-es";
import companyMixin from "@state/company";
import NpsWidget from "@components/help/NpsWidget.vue";
import { CheckCircleIcon, ExclamationCircleIcon } from "@vue-hero-icons/solid";
import CompanyForm from "@blocks/company/Form.vue";
import VacancyHeader from "@blocks/vacancy/VacancyHeader.vue";
import ChangeableStatus from "@components/changeable/Status.vue";
import { ValidationObserver } from "vee-validate";

import { useCompanyForm } from "@state/company/form";
import { useCurriculum } from "@state/user/curriculum";
import { useDomain } from "@state/domain/domain";
import { useApplications } from "@state/user/applications";
import { useVacancies } from "@state/vacancy/vacancies";
import { useChangeable } from "@state/behaviours/changeable";
import { trackOnce, enrollPayload, uuid, APPLICATION_COMPLETE } from "@/plugins/tracking";

import * as FormComponents from "@components/forms";
import FormSetPcd from "@components/forms/set/Pcd.vue";
import FormSetProfessionalExperience from "@components/forms/set/ProfessionalExperience.vue";
import FormSetAcademic from "@components/forms/set/Academic.vue";
import FormSetLanguages from "@components/forms/set/Languages.vue";
import FormSetComputing from "@components/forms/set/Computing.vue";

export default {
  mixins: [companyMixin],
  components: {
    ...FormComponents,
    ChangeableStatus,
    VacancyHeader,
    CompanyForm,
    ValidationObserver,
    CheckCircleIcon,
    ExclamationCircleIcon,
    FormSetPcd,
    NpsWidget,
    FormSetProfessionalExperience,
    FormSetAcademic,
    FormSetLanguages,
    FormSetComputing,
    CheckCircleIcon,
    ExclamationCircleIcon,
  },
  props: {
    vacancy_id: Number | String,
  },
  data() {
    return {
      internalVacancy: null,
      isEnrolling: true,
      isLoading: true,
      changeCounter: 0,
      isSaving: false,
      alreadApplied: false,
      stateSubscribe: null,
      sections: {},
      success: false,
      show: false,
      form: {},
      messages: {},
      emittedByZipCodeField: false,
      blockingApplications: false,
      blockingAlreadyApplied: false,
      requireOrigin: false,
      originOptions: [],
      candidatureOriginId: null,
      // Mesmo event_id no POST de candidatura (gravado com a atribuição) e no pixel de application_complete
      trackingEventId: uuid(),
    };
  },
  setup() {
    const curriculum = useCurriculum();
    const domain = useDomain();
    const forms = useCompanyForm();
    const applications = useApplications();
    const vacancies = useVacancies();
    const changeable = useChangeable();

    return {
      vacancies,
      curriculum,
      domain,
      forms,
      applications,
      changeable,
    };
  },
  mounted() {
    this.isEnrolling = true;

    if (this.vacancy_id) {
      this.vacancies.read(this.vacancy_id).then(({ data }) => {
        this.internalVacancy = data;
      }).catch((error) => {
        this.$router.replace({name: 'home'});
      });
    }

    if (this.$auth.check()) {
      this.applications
        .enroll(this.vacancy_id, this.enrollRequest())
        .then((response) => {
          this.applyEnrollResponse(response, false);
        })
        .finally(() => {
          this.isEnrolling = false;
        });
    } else {
      this.$root.loading = false;
      this.$router.push({
        name: "register1",
        params: { vacancy_id: this.vacancy_id },
      });
    }
  },
  destroyed() {
    if (_.isFunction(this.stateSubscribe)) {
      this.stateSubscribe();
    }
  },
  computed: {
    hasFields() {
      return (
        this.form && _.has(this.form, "fields") && this.form.fields.length > 0
      );
    },
    hasSections() {
      return (
        this.form &&
        _.has(this.form, "sections") &&
        this.form.sections.length > 0
      );
    },
    hasPcd() {
      return (
        _.has(this.form, "sections") && _.includes(this.form.sections, "pcd")
      );
    },
    hasProfessionalSection() {
      return (
        _.has(this.form, "sections") &&
        _.includes(this.form.sections, "professional")
      );
    },
    hasAcademicSection() {
      return (
        _.has(this.form, "sections") &&
        _.includes(this.form.sections, "academic")
      );
    },
    hasComputingSection() {
      return (
        _.has(this.form, "sections") &&
        _.includes(this.form.sections, "computing")
      );
    },
    hasLanguagesSection() {
      return (
        _.has(this.form, "sections") &&
        _.includes(this.form.sections, "languages")
      );
    },
    isEnrollBlocked() {
      return !this.hasFields && !this.hasSections;
    },
    canContinue() {
      return this.success || this.isEnrollBlocked;
    },
  },
  methods: {
    applyEnrollResponse(response, showNps) {
      if (response.data.success) {
        this.requireOrigin = false;
        this.isEnrolling = false;
        this.success = true;
        this.trackApplicationComplete();

        setTimeout(() => {
          this.$router.replace({
            path: this.$route.path,
            query: { showNps: showNps },
          });
        }, 1500);
        this.show = true;
        return;
      }

      if (response.data.require_origin) {
        this.requireOrigin = true;
        this.originOptions = response.data.origins || [];
        this.success = false;
        this.messages = response.data.messages || [];
        this.isEnrolling = false;
        this.show = true;
        return;
      }

      this.requireOrigin = false;

      if (response.data.hasOwnProperty("blocking_applications") && response.data.blocking_applications === true) {
        this.blockingApplications = true;
      }

      if (response.data.hasOwnProperty("blocking_already_applied") && response.data.blocking_already_applied === true) {
        this.blockingAlreadyApplied = true;
      }

      if (_.indexOf(response.data.messages, "errors.enroll.already-applied") >= 0) {
        this.alreadApplied = true;
      }

      this.success = false;
      this.messages = response.data.messages;
      this.form = response.data.form;

      if (!this.stateSubscribe) {
        this.stateSubscribe = this.changeable.$subscribe(() => {
          const poll_changes = this.changeable.poolChanges("curriculum");
          this.changeCounter = poll_changes.counter;

          if (poll_changes.counter >= 1) {
            this.isSaving = true;

            this.curriculum.persist(poll_changes.changes).then(({ data }) => {
              if (data.success) {
                this.changeable.cleanUpAfterSave();
                this.changeCounter = 0;
              }
              this.isSaving = false;
            });
          }
        });
      }

      if (this.form && _.has(this.form, "fields")) {
        this.forms.fields(this.form.name, true, ...this.form.fields).then(() => {
          for (let i in this.forms.curriculum.sections) {
            this.$set(this.sections, i, this.forms.curriculum.sections[i]);
          }

          this.show = true;
        });
      }

      this.show = true;
    },
    confirmOrigin() {
      this.$refs.originRules.validate().then((valid) => {
        if (!valid) {
          return;
        }

        this.isEnrolling = true;
        this.applications
          .enroll(this.vacancy_id, this.enrollRequest({
            candidature_origin_id: this.candidatureOriginId,
          }))
          .then((response) => {
            this.applyEnrollResponse(response, true);
          })
          .finally(() => {
            this.isEnrolling = false;
          });
      });
    },
    enrollRequest(extra = {}) {
      const tracking = enrollPayload(this.trackingEventId);

      return {
        ...(tracking ? { tracking } : {}),
        ...extra,
      };
    },
    // Só com sucesso do enroll; uma vez por vaga na sessão (reload cai em blocking_already_applied)
    trackApplicationComplete() {
      const vacancy = this.internalVacancy || {};

      trackOnce(`ac:${this.vacancy_id}`, APPLICATION_COMPLETE, {
        event_id: this.trackingEventId,
        job_id: vacancy.id || this.vacancy_id,
        job_title: vacancy.occupation || vacancy.seo_title,
      });
    },
    continueEnroll() {
      if (this.canContinue) {
        this.$router.push({
          name: "profile-edit",
        });
      } else {
        this.isEnrolling = true;

        this.$refs.enrollStepRules.validate().then((success) => {
          if (success) {
            const poll_changes = this.changeable.poolChanges("curriculum");
            this.changeCounter = poll_changes.counter;

            // SAVE PARTIAL CURRICULUM
            this.isSaving = true;

            this.curriculum.persist(poll_changes.changes).then(({ data }) => {
              if (data.success) {
                this.changeable.cleanUpAfterSave();
                this.changeCounter = 0;
              }

              this.applications
                .enroll(this.vacancy_id, this.enrollRequest())
                .then((response) => {
                  this.applyEnrollResponse(response, true);
                })
                .finally(() => {
                  this.isSaving = false;
                  this.isEnrolling = false;
                });
            });
          } else {
            this.inputFocus();
            this.isEnrolling = false;
            return;
          }
        });
      }
    },
    inputFocus() {
      this.$nextTick(() => {
        const el = this.$el.querySelector(".is-invalid");
        if(el) {
          el.scrollIntoView();
        }
        return;
      });
    },
    goToApplication() {
      if (this.company.config.sj) {
        this.$router.push({
          name: "application-progress",
          params: {
            application_id: this.vacancy_id,
            sj_id: this.vacancy_id,
          },
        });
      } else {
        this.$router.push({
          name: "application-progress",
          params: { application_id: this.vacancy_id },
        });
      }
    },
    getTranslatedHelp(content = '') {
      if (content.startsWith("$t:")) {
        return this.$t(content.replace('$t:', ''));
      }

      return content
    }
  },
  watch: {
    // "curriculum.address.zip_code": function (zipcode) {
    //   this.emittedByZipCodeField = true;
    //   this.curriculum
    //     .setAddressBasedOnZipcode(zipcode)
    //     .then((response) => {
    //       if (response) {
    //         this.$nextTick(() => {
    //           this.curriculum.address.street_type = response.street_type;
    //           this.curriculum.address.street = response.street;
    //           this.curriculum.address.district = response.district;
    //           this.curriculum.address.country = response.country;
    //           this.curriculum.address.state = response.state;
    //           this.curriculum.address.latitude = response.latitude;
    //           this.curriculum.address.longitude = response.longitude;
    //           // NECESSARY BECAUSE THE CITIES LIST MUST BE LOADED FIRST
    //           this.$nextTick(() => {
    //             this.curriculum.address.city = response.city;
    //           });
    //         });
    //       }
    //     })
    //     .finally(() => (this.emittedByZipCodeField = false));
    // },
    "curriculum.address.state": function (uf) {
      if (!this.emittedByZipCodeField) {
        this.curriculum.address.city = "";
      }
      this.domain.load(`cities/${uf}`, "cities");
    },
  },
};
</script>
